import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ReferenceUploader } from "./ReferenceUploader";
import { Loader2, Video } from "lucide-react";

const TEMPLATES = [
  { id: "unboxing", label: "Unboxing", hint: "Opens the box, lifts out the product, shows the charger" },
  { id: "product_launch", label: "Product launch", hint: "Launch hook, key design and feature, CTA" },
  { id: "selfie_review", label: "Selfie review", hint: "Handheld selfie-style honest review" },
];
const field = "w-full text-sm border border-slate-300 rounded-md px-2.5 py-2 bg-white text-slate-900 placeholder:text-slate-400";

export const UgcCreateButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ template: "unboxing", brand_id: "", influencer_id: "", product_name: "", brief: "", cta_text: "", duration_target_sec: 40, attachments: [] });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands").then((r) => r.data) });
  const { data: infs = [] } = useQuery({ queryKey: ["influencers"], queryFn: () => api.get("/influencers").then((r) => r.data) });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const hasProduct = f.attachments.some((a) => a.kind === "product" || a.kind === "charger");
  const ready = f.brand_id && f.influencer_id && f.product_name.trim() && f.brief.trim().length >= 20 && hasProduct;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/ugc/create", {
        ...f, duration_target_sec: Number(f.duration_target_sec),
        attachments: f.attachments.map((a) => ({ id: a.id, kind: a.kind || "product" })),
      });
      toast.success(`UGC script ready — ${data.content.scenes.length} scenes. Review, then render.`);
      setOpen(false);
      onCreated(data.content);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the UGC video");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button data-testid="ugc-open-btn" size="sm" className="h-8 text-xs w-full mb-3 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setOpen(true)}>
        <Video className="w-3.5 h-3.5 mr-1.5" /> New UGC video (lip-synced presenter)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ugc-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900 border-slate-200">
          <DialogHeader><DialogTitle>UGC presenter video</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            {TEMPLATES.map((t) => (
              <button key={t.id} data-testid={`ugc-template-${t.id}`} onClick={() => setF((s) => ({ ...s, template: t.id }))}
                className={`text-left rounded-md border p-2.5 transition-colors ${f.template === t.id ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{t.hint}</div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <select data-testid="ugc-brand" className={field} value={f.brand_id} onChange={set("brand_id")}>
              <option value="">Brand…</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select data-testid="ugc-presenter" className={field} value={f.influencer_id} onChange={set("influencer_id")}>
              <option value="">Presenter…</option>
              {infs.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <input data-testid="ugc-product" className={field} placeholder="Product name (e.g. Signia Styletto IX)" value={f.product_name} onChange={set("product_name")} />
            <input data-testid="ugc-cta" className={field} placeholder="CTA (e.g. HearClear.in / +91 99719 25446)" value={f.cta_text} onChange={set("cta_text")} />
          </div>
          <textarea data-testid="ugc-brief" className={`${field} mt-3 h-24`} placeholder="Approved facts only: what the product is, real features, who it's for. No invented specs or prices." value={f.brief} onChange={set("brief")} />
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
            Length
            <select data-testid="ugc-duration" className="border border-slate-300 rounded px-1.5 py-1" value={f.duration_target_sec} onChange={set("duration_target_sec")}>
              {[20, 30, 40, 45].map((d) => <option key={d} value={d}>{d}s</option>)}
            </select>
          </div>
          <div className="mt-3 text-xs uppercase font-mono tracking-widest text-slate-500">Genuine product photos (required)</div>
          <ReferenceUploader attachments={f.attachments} onChange={(fn) => setF((s) => ({ ...s, attachments: fn(s.attachments) }))} />
          <p className="text-[11px] text-slate-500 mt-2">
            The voice is created from the script and the presenter's face, lips and head are animated from that same voice, so lips match every word. Product shape and logo come from your photo but are re-drawn by AI, so check them in review before publishing.
          </p>
          <Button data-testid="ugc-create-btn" onClick={create} disabled={!ready || busy} className="mt-3 w-full">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Writing script…</> : "Write script & storyboard"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
